import { colorCodes } from './config';
import * as Plot from '@observablehq/plot';
import { useEffect, useRef } from 'react';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import styles from './Chart.module.scss';

type ChartData = {
  id: string;
  label: string;
  name: string;
  values: {
    date: string;
    value: number;
  }[];
}[];

type SelectedCategories = {
  [key: string]: boolean;
};

type Props = {
  selectedCategories: SelectedCategories;
  chartData: ChartData;
  isLoading: boolean;
};

const FundDetailsPerformanceTabCumulativeChart = ({
  selectedCategories,
  chartData,
  isLoading,
}: Props) => {
  const { localizeDate } = useHelpers();
  const { t } = useI18n();
  const chartWrapper = useRef(null);
  const getConvertedValueForChartData = (value, category) => {
    return {
      date: new Date(value.date),
      value: value.value,
      category: category.label,
      show: !!selectedCategories?.[category?.label],
    };
  };

  useEffect(() => {
    if (!chartData) return;

    const data = chartData.reduce(
      (data, category) => [
        ...data,
        ...category.values.map((value) => getConvertedValueForChartData(value, category)),
      ],
      []
    );

    const values: Array<number> = data.map(({ value }) => value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    const plot = Plot.plot({
      x: {
        ticks: 8,
      },
      y: {
        grid: true,
        label: t('fundDetailsPerformanceChartValue'),
        ticks: 6,
      },
      marginBottom: 40,
      marginLeft: 60,
      width: chartWrapper.current?.offsetWidth,
      height: chartWrapper.current?.offsetHeight,
      color: {
        range: colorCodes,
      },
      marks: [
        Plot.ruleY([minValue]),

        Plot.areaY(data, {
          x: 'date',
          y1: minValue,
          y2: 'value',
          z: 'category',
          fill: 'category',
          opacity: 0.05,
          filter: (d) => d.show,
        }),

        Plot.lineY(data, {
          x: 'date',
          y: 'value',
          z: 'category',
          stroke: 'category',
          strokeWidth: (d) => (d.show ? 2 : 1),
          opacity: (d) => (d.show ? 1 : 0.25),
          sort: (d) => d.show,
        }),

        Plot.ruleX(
          data,
          Plot.pointerX({
            x: 'date',
            y1: minValue,
            y2: maxValue,
            z: 'category',
            strokeWidth: 1,
            opacity: 0.2,
            maxRadius: 999,
          })
        ),

        ...chartData.map((category) =>
          Plot.dot(
            category.values.map((value) => getConvertedValueForChartData(value, category)),
            Plot.pointerX({
              x: 'date',
              y: 'value',
              z: 'category',
              fill: 'category',
              r: 4,
              strokeWidth: 0,
              filter: (d) => d.show,
              maxRadius: 999,
            })
          )
        ),

        Plot.tip(
          data,
          Plot.pointerX({
            x: 'date',
            y: 'value',
            z: 'category',
            strokeWidth: 0,
            filter: (d) => d.show,
            title: (d) =>
              [
                localizeDate(d.date),
                ...data
                  .filter((data) => data.date.getTime() === d.date.getTime())
                  .map((data) => `${data.category}: ${data.value}`),
              ].join('\n'),
            pointerSize: 0,
            textPadding: 12,
            fontSize: 12,
            lineHeight: 1.5,
            maxRadius: 999,
          })
        ),
      ],
    });
    chartWrapper.current.append(plot);

    return () => plot.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, selectedCategories, getConvertedValueForChartData]);

  return (
    <div className="position-relative">
      <div ref={chartWrapper} className={styles['chart-wrapper']} />
      {isLoading && (
        <div className={styles['loading-overlay']}>
          <BaseSkeleton height="100%" />
        </div>
      )}
    </div>
  );
};

export default FundDetailsPerformanceTabCumulativeChart;
